import React, { useCallback, useMemo, useState } from 'react';
import { useKeywordStatsRecompute } from '../../../shared/api/hooks/monitor/keyword/useKeywordStatsRecompute';
import './ProjectsPreviewPage.scss';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import ProjectPreviewSkeleton from './components/ProjectPreviewSkeleton';
import { PROJECTS } from '../../../shared/routes/path';
import { Trans, useTranslation } from 'react-i18next';
import Title from '../../../shared/components/title/Title';
import PromptsTable from '../../../shared/components/prompts-table/PromptsTable';
import { useFilters } from '../../../shared/contexts/filters/FiltersContext';
import useFiltersInitializing from '../../../shared/hooks/filters/UseFiltersInitializing';
import useMonitorPeriodAndChatType from '../../../shared/hooks/projects/preview/UseMonitorPeriodAndChatType';
import useProjectNavigate from '../../../shared/hooks/projects/preview/UseProjectNavigate';
import FilterPanel from '../../../shared/components/filter-panel/FilterPanel';
import MoreBtn from '../../../shared/components/common/more-btn/MoreBtn';
import { Button } from 'primereact/button';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import useExport from '../../../shared/hooks/global/UseExport';
import useChangeName from '../../../shared/hooks/projects/UseChangeName';
import useRemoveProject from '../../../shared/hooks/projects/UseRemoveProject';
import useExportData from '../../../shared/hooks/projects/UseExportData';
import usePermission from '../../../shared/hooks/global/UsePermission';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../../shared/components/common/mobile-menu/MobilePageHeader';
import useCanManage from '../../../shared/hooks/account/UseCanManage';
import useChooseKeywords from '../../../shared/hooks/projects/UseChooseKeywords';
import { LinkTrackingTable, ProjectStatsTiles, ShareOfVoiceChart } from './components';
import { useGetTags } from '../../../shared/api/hooks/tags/useGetTags';
import { useGetMonitorsForProject } from '../../../shared/api/hooks/monitor/useGetMonitorsForProject';
import { useGetCommonKeywordsWithTags } from '../../../shared/api/hooks/monitor/keyword/useGetCommonKeywordsWithTags';
import { useGetKeywordsDailyStatsForProject } from '../../../shared/api/hooks/monitor/keyword/stats/useGetKeywordsDailyStatsForProject';
import { useGetSourcesForProject } from '../../../shared/api/hooks/monitor/data/sources/useGetSourcesForProject';
import { ProjectPreviewEmptyState } from './components/empty-state/ProjectPreviewEmptyState';
import { useGetProjectDailyStats } from '../../../shared/api/hooks/project/stats/useGetProjectDailyStats';
import { useGetEvents } from '../../../shared/api/hooks/monitor/events/useGetEvents';
import BrandPositionChart from './components/brand-position-chart/BrandPositionChart';
import { BrandsTable } from './components/brand-table/BrandsTable';
import ShareOfVoiceBarChart from './components/sov-bar-chart/ShareOfVoiceBarChart';
import { useGetProjectAllBrandsStats } from '../../../shared/api/hooks/project/stats/useGetProjectAllBrandsStats';
import { useGetProjectSovPerModel } from '../../../shared/api/hooks/project/stats/useGetProjectSovPerModel';
import { useGetLiveBrandKeys } from '../../../shared/api/hooks/project/stats/useGetLiveBrandKeys';
import { useQueryClient } from '@tanstack/react-query';
import { GET_PROJECTS_LIST_KPIS } from '../../../shared/api/query/project/stats/keys';
import { hasProjectStats } from '../../../shared/helpers/project/ProjectHelper';
import {
  expandSelectedChatTypes,
  expandSelectedChatTypesToMonitorTypes,
} from '../../../shared/helpers/MonitorTypeHelpers';
import { useChartColors } from './hooks/UseChartColors';
import { useKeywordMapper } from './hooks/UseKeywordMapper';
import { usePromptsData } from './hooks/UsePromptsData';
import { useProjectStatsMatrix } from './hooks/UseProjectStatsMatrix';
import { useShareOfVoiceData } from './hooks/UseShareOfVoiceData';
import { useAllModelsStatsMatrix } from './hooks/UseAllModelsStatsMatrix';
import InsightsBox from './components/insights/InsightsBox';
import { useCurrentBrandGeneralStats } from './hooks/UseCurrentBrandGeneralStats';
import { useVisibilityTexts } from './hooks/UseVisibilityTexts';
import { findTagIdInKeyword, getBrandDisplayName, resolveBrandKey } from '../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../shared/helpers/tags/TagsHelper';
import { useEffectiveAddons } from '../../../shared/contexts/effective-features/EffectiveFeaturesContext';
import { TAG_NAME } from '../../../shared/utils/urls';
import { useGetReport } from '../../../shared/api/hooks/report/useGetReport';
import html2pdf from 'html2pdf.js';
import { PdfReportResponse } from '../../../shared/models/report/PdfReportResponse';
import { useFirstViewTracking } from './hooks/UseFirstViewTracking';
import { useGetProject } from '../../../shared/api/hooks/project/useGetProject';
import ChangeKeywordBox from '../../../shared/components/change-keyword-box/ChangeKeywordBox';
import { useModelsWithoutData } from './hooks/UseModelsWithoutData';
import { useGetAnomalyIncidents } from '../../../shared/api/hooks/anomaly-detection/useGetAnomalyIncidents';
import { usePromptGroups } from '../../../shared/hooks/prompt/UsePromptGroups';
import { PromptGroupItem } from '../../../shared/components/prompts-table/PromptsTable';
import { Dropdown } from 'primereact/dropdown';
import PromptGroupDropdown from '../../../shared/components/prompt-group-dropdown/PromptGroupDropdown';
import usePromptGroupId, { ALL_PROMPT_GROUPS_ID } from '../../../shared/hooks/filters/UsePromptGroupId';
import { Dialog } from 'primereact/dialog';
import { regenerateSuggestedQuestions } from '../../../shared/services/projects/ProjectService';
import useToast from '../../../shared/hooks/global/UseToast';

const ProjectsPreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  let { projectId, type, period, chat } = useParams();
  const projectIdNumber = Number(projectId);

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { monitorPeriod, range, selectedChatType, selectedChatTypes } = useFilters();
  const filtersInitializing = useFiltersInitializing();
  const { projectResponse, accountResponse, refetchData, refetchAccountSilently, refetchProjectsSilently } = useAccount();
  const { canManage } = useCanManage();
  const { canExport } = useExport();

  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingBrandScore, setIsDownloadingBrandScore] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerateLanguage, setRegenerateLanguage] = useState<{ name: string; type: string } | null>(
    null
  );
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { showToast, toastElement } = useToast();

  const regenerateLanguageOptions = useMemo(
    () => [
      { name: 'PL', type: 'PL' },
      { name: 'EN', type: 'EN' },
    ],
    []
  );
  const [searchParams] = useSearchParams();
  const hasExplicitModelFilter = searchParams.get('models') !== null;
  // The "per model" breakdown carries no information with a single model selected.
  // `selectedChatType` won't catch this — with a `?models=` filter it stays `ALL`.
  const isSingleModelSelected = hasExplicitModelFilter && selectedChatTypes.length === 1;
  const filterChatTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0) return undefined;
    return Array.from(expandSelectedChatTypes(selectedChatTypes));
  }, [hasExplicitModelFilter, selectedChatTypes]);
  // The ads/shopping columns' server-side merge filters on md.type (MonitorTypes), so pass the
  // expanded MonitorTypes (not the ChatTypes used elsewhere) as `models` to the keyword-stats call.
  const filterMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0) return undefined;
    return expandSelectedChatTypesToMonitorTypes(selectedChatTypes);
  }, [hasExplicitModelFilter, selectedChatTypes]);
  const { promptGroupId } = usePromptGroupId();
  const hasPromptGroupsAddon = !!accountResponse?.addons?.includes('PROMPT_GROUP');
  const hasAnomalyDetectionAddon = !!accountResponse?.addons?.includes('ANOMALY_DETECTION');
  const canUseAnomaliesForSelection = !hasExplicitModelFilter || selectedChatTypes.length === 1;
  const canShowAnomalyMarkers = hasAnomalyDetectionAddon && monitorPeriod === 'WEEKLY' && canUseAnomaliesForSelection;
  const { data: promptGroups } = usePromptGroups(projectIdNumber);

  const startDate = range?.[0] || null;
  const endDate = range?.[1] || null;

  const {
    project,
    refetch: reloadProject,
    isLoading: isProjectLoading,
  } = useGetProject(projectIdNumber);

  const { refetch: reloadReport } = useGetReport(projectIdNumber, startDate, endDate, period);

  const { tags, refetch: reloadTags, isLoading: isTagsLoading } = useGetTags(projectIdNumber);
  const {
    monitorPrompts,
    isLoading: isMonitorPromptsLoading,
    refetch: reloadMonitorPrompts,
  } = useGetMonitorsForProject(projectIdNumber);

  const filteredMonitorPrompts = useMemo(() => {
    if (!monitorPrompts) return null;
    if (promptGroupId === ALL_PROMPT_GROUPS_ID) return monitorPrompts;
    return monitorPrompts.filter((p) => p.promptGroupId === promptGroupId);
  }, [monitorPrompts, promptGroupId]);

  const {
    commonKeywords,
    isLoading: isCommonKeywordsLoading,
    refetch: reloadCommonKeywords,
  } = useGetCommonKeywordsWithTags(projectIdNumber);

  const {
    keywordsDailyStatsForProject,
    isLoading: isKeywordDailyStatsForProjectLoading,
    isRefreshing: isKeywordDailyStatsForProjectRefreshing,
    refetch: reloadKeywordDailyStatsForProject,
  } = useGetKeywordsDailyStatsForProject(
    projectIdNumber,
    selectedChatType,
    monitorPeriod,
    startDate,
    endDate,
    filteredMonitorPrompts,
    filterMonitorTypes
  );

  const {
    projectDailyStats,
    isLoading: isProjectDailyStatsLoading,
    isRefreshing: isProjectDailyStatsRefreshing,
    refetch: reloadProjectDailyStats,
  } = useGetProjectDailyStats(
    projectIdNumber,
    filteredMonitorPrompts,
    monitorPeriod,
    selectedChatType,
    startDate,
    endDate,
    filterChatTypes
  );

  const {
    events,
    isLoading: isEventsLoading,
    refetch: reloadEvents,
  } = useGetEvents(projectIdNumber, monitorPeriod, selectedChatType, startDate, endDate);

  const {
    brandScoreAnomalies,
    averagePositionAnomalies,
    visibilityAnomalies,
    metricSemantics,
    isLoading: isAnomalyIncidentsLoading,
    refetch: reloadAnomalyIncidents,
  } = useGetAnomalyIncidents(
    projectIdNumber,
    startDate,
    endDate,
    new Set(filteredMonitorPrompts ? filteredMonitorPrompts.map(prompt => prompt.keyword) : []),
    filterChatTypes,
    canShowAnomalyMarkers
  );

  const {
    allBrandsStatsResponse,
    isLoading: isAllBrandsStatsResponseLoading,
    isRefreshing: isAllBrandsStatsResponseRefreshing,
    refetch: reloadAllBrandsStatsResponse,
  } = useGetProjectAllBrandsStats(projectIdNumber, filteredMonitorPrompts, selectedChatType, monitorPeriod, startDate, endDate, filterChatTypes);

  // Per-(model, brand) SoV for the "SoV by AI Chatbots" bars, pooled like this page's own pie — so a
  // model's column reads exactly as the pie does once that model is picked in the filter.
  const {
    sovPerModel,
    isLoading: isSovPerModelLoading,
    isRefreshing: isSovPerModelRefreshing,
    refetch: reloadSovPerModel,
  } = useGetProjectSovPerModel(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    startDate,
    endDate,
    filterChatTypes,
    'pooled'
  );

  // Live brand keys (data under current models) — hide orphan brands from the brand-selection surfaces.
  const { liveBrandKeys } = useGetLiveBrandKeys(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    startDate,
    endDate,
    filterChatTypes
  );

  const { sourcesForProject, refetch: reloadSourcesForProject } = useGetSourcesForProject(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    startDate,
    endDate
  );

  const onRefresh = useCallback(async () => {
    // The ProjectsPage KPI columns (brand score / position / visibility) are computed for the
    // project's brand (firmName), so a brand switch here must refresh them too.
    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KPIS] });
    await Promise.all([
      reloadProject(),
      reloadMonitorPrompts(),
      reloadTags(),
      reloadCommonKeywords(),
      reloadKeywordDailyStatsForProject(),
      reloadProjectDailyStats(),
      reloadEvents(),
      reloadAnomalyIncidents(),
      reloadAllBrandsStatsResponse(),
      reloadSovPerModel(),
      reloadSourcesForProject(),
      refetchAccountSilently(),
      refetchProjectsSilently(),
    ]);
  }, [
    reloadProject,
    reloadMonitorPrompts,
    reloadTags,
    reloadCommonKeywords,
    reloadKeywordDailyStatsForProject,
    reloadProjectDailyStats,
    reloadEvents,
    reloadAnomalyIncidents,
    reloadAllBrandsStatsResponse,
    reloadSovPerModel,
    reloadSourcesForProject,
    refetchAccountSilently,
    refetchProjectsSilently,
    queryClient,
  ]);

  // Przeliczanie statystyk marki wybranej (po włączeniu jej nazwy) — tabele pokazują wtedy loadery.
  const { selectedBrandPending: pendingBrands, isSelectedBrandRecomputing: isRecomputing } =
    useKeywordStatsRecompute(projectIdNumber);
  const { confirmNewProjectKeywords } = useChooseKeywords({
    refreshProjects: onRefresh,
    keywordsPage: useEffectiveAddons().includes('KEYWORDS_PAGE'),
  });

  const { editName } = useChangeName({ onRefresh });

  const { confirmRemove } = useRemoveProject({
    refreshProjects: () => {
      refetchData();
      navigate(PROJECTS);
    },
  });

  const downloadReport = async () => {
    const result = await reloadReport();
    if (!result || result.status !== 'success' || !result.data) {
      return;
    }

    const data = result.data as PdfReportResponse;
    let template = data.template as string;

    (data.chartAttachments || []).forEach((attachment: string) => {
      const [filename, base64] = attachment.split(': ');
      if (!filename || !base64) return;
      const cid = `cid:${filename.trim()}`;
      template = template.split(cid).join(`data:image/png;base64,${base64.trim()}`);
    });

    const parser = new DOMParser();
    const doc = parser.parseFromString(template, 'text/html');

    const images = doc.querySelectorAll('img');
    images.forEach((img) => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.style.margin = '0 auto';
    });

    template = doc.body.innerHTML;

    const styledTemplate = `
    <div style="
      width: 190mm; 
      max-width: 190mm; 
      margin: 0 auto; 
      padding: 10mm;
      box-sizing: border-box;
      font-family: Arial, sans-serif;
    ">
      ${template}
    </div>
  `;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = styledTemplate;

    await html2pdf()
      .from(wrapper)
      .set({
        filename: 'report.pdf',
        html2canvas: {
          scale: 2,
          useCORS: true,
          scrollY: 0,
          scrollX: 0,
          windowWidth: 1200,
          letterRendering: true,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
        },
        pagebreak: {
          mode: ['avoid-all', 'css'],
          avoid: '.report-section',
        },
      } as any)
      .save();
  };

  useProjectNavigate(projectIdNumber);
  useMonitorPeriodAndChatType(type, period, chat, projectIdNumber, accountResponse);
  const { exportStats, exportSources, exportBrandScore } = useExportData(projectIdNumber);
  const projectsAccess = usePermission('PROJECTS');
  const isMobile = useIsMobile();

  const firmName = useMemo(() => {
    if (!project) {
      return '';
    }

    return project.firmName || '';
  }, [project]);

  // Canonical key for matching against stats rows (tag key / normalized lowercase) — raw firmName
  // may keep display casing and would silently miss every row.
  const firmBrandKey = useMemo(() => resolveBrandKey(firmName, tags), [firmName, tags]);
  const hasBrandGrouping = useEffectiveAddons().includes('KEYWORDS_PAGE');
  const defaultBrandKey = useMemo(
    () => resolveBrandKey(project?.defaultKeyword ?? '', tags),
    [project?.defaultKeyword, tags]
  );

  const firmNameOrg = useMemo(() => {
    if (!firmName || !project) {
      return '';
    }

    const tagId = commonKeywords ? findTagIdInKeyword(firmName, commonKeywords) : null;
    const brandName = getBrandDisplayName(firmName, project.firmNameOrg, commonKeywords, hasBrandGrouping);
    return tags ? getTagLabel(tags, tagId, brandName) : brandName;
  }, [project, tags, commonKeywords, firmName, hasBrandGrouping]);

  // "Collecting" = a prompt is actively producing its first data now (PENDING/REFRESHING). Deferred or
  // terminal statuses (QUEUED_FOR_NEXT_DAY, ERROR, DELETED, TO_DELETE) must NOT count — otherwise the
  // page gets stuck on the "Computing" loader for data that won't arrive today.
  const hasAnyPendingQuestions = !!monitorPrompts?.some((promptData) =>
    ['PENDING', 'REFRESHING'].includes(promptData.status)
  );

  // Once ANY prompt has reached ACTIVE (querying ran at least once), the project is past its first
  // collection — a still-pending/queued prompt must NOT reblock the whole stats view with the
  // "computing" loader. Only a truly fresh project (pending, nothing active yet) shows collecting;
  // otherwise we show the real state (data, or the standard empty state).
  const hasAnyActivePrompt = !!monitorPrompts?.some((promptData) => promptData.status === 'ACTIVE');
  const isInitialCollecting = hasAnyPendingQuestions && !hasAnyActivePrompt;

  const nonEmptyPromptGroups = useMemo(() => {
    if (!promptGroups || !monitorPrompts) return [];
    const groupIdsWithPrompts = new Set(
      monitorPrompts
        .map((p) => p.promptGroupId)
        .filter((id): id is number => id != null)
    );
    return promptGroups.filter((g) => groupIdsWithPrompts.has(g.id));
  }, [promptGroups, monitorPrompts]);

  const keywordsMap = useKeywordMapper(commonKeywords, monitorPrompts);
  const firmNameLabel = useMemo(() => {
    // For tag-linked brands (firmName starts with TAG_NAME prefix), keywordsMap
    // maps the raw tag key to itself (chatbeat_tag_name_X → chatbeat_tag_name_X),
    // which is not a useful display label. Skip the map lookup for tag prefixes
    // and go directly to getTagLabel, mirroring exactly what getPieChartWithOthers does.
    if (!firmName.startsWith(TAG_NAME)) {
      const mappedLabel = keywordsMap.get(firmName);
      if (mappedLabel) return mappedLabel;
    }

    // firmName is a tag-prefixed keyword (chatbeat_tag_name_<tagId>).
    // Resolve the same way getPieChartWithOthers does so that firmNameLabel
    // matches the display label stored in shareOfVoiceData slice names.
    const tagId = commonKeywords ? findTagIdInKeyword(firmName, commonKeywords) : null;
    if (tagId && tags) {
      return getTagLabel(tags, tagId, firmName);
    }

    return firmName;
  }, [keywordsMap, firmName, commonKeywords, tags]);

  const shareOfVoiceData = useShareOfVoiceData(
    allBrandsStatsResponse,
    keywordsMap,
    firmName,
    commonKeywords,
    tags
  );
  const promptsData = usePromptsData(
    isKeywordDailyStatsForProjectLoading,
    isEventsLoading,
    keywordsDailyStatsForProject,
    keywordsMap,
    filteredMonitorPrompts,
    selectedChatType,
    accountResponse,
    commonKeywords,
    tags,
    selectedChatTypes
  );
  const projectStatsMatrix = useProjectStatsMatrix(
    isProjectDailyStatsLoading,
    isMonitorPromptsLoading,
    isEventsLoading,
    period,
    projectDailyStats
  );
  // chatType → canonicalKeyword → SoV%. Keys are tag-resolved, i.e. the same space as
  // allBrandsStatsResponse / firmName, so they line up with the matrix's brand selection.
  const perModelSov = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    sovPerModel.forEach(({ chatType, keyword, shareOfVoice }) => {
      if (!map.has(chatType)) map.set(chatType, new Map());
      map.get(chatType)!.set(keyword, shareOfVoice);
    });
    return map;
  }, [sovPerModel]);

  const allModelsStatsMatrix = useAllModelsStatsMatrix(
    isProjectDailyStatsLoading || isSovPerModelLoading,
    isMonitorPromptsLoading,
    isEventsLoading,
    firmName,
    accountResponse,
    shareOfVoiceData,
    keywordsMap,
    perModelSov,
    hasExplicitModelFilter,
    selectedChatTypes
  );
  const modelsWithoutData = useModelsWithoutData(
    accountResponse,
    keywordsDailyStatsForProject,
    isKeywordDailyStatsForProjectLoading,
    range,
    // Superadmin on a client's project → resolve models from the OWNER's features.
    project?.ownerFeatures
  );
  const brandColors = useChartColors(shareOfVoiceData, firmNameLabel, keywordsMap);
  const currentBrandGeneralStats = useCurrentBrandGeneralStats(firmName, allBrandsStatsResponse, tags);
  const monitorHeaders = [t('brandScore'), t('averagePosition')];
  const visibilityTexts = useVisibilityTexts(
    firmName,
    firmNameLabel,
    currentBrandGeneralStats,
    promptsData,
    selectedChatType,
    accountResponse,
    allModelsStatsMatrix.matrix,
    tags,
    commonKeywords
  );

  const handleExport = async () => {
    if (isDownloading || !canExport) {
      return;
    }

    try {
      setIsDownloading(true);
      await exportStats();
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDailyStatsExport = async () => {
    if (isDownloadingBrandScore || !canExport) {
      return;
    }

    try {
      setIsDownloadingBrandScore(true);
      await exportBrandScore(monitorPeriod);
    } finally {
      setIsDownloadingBrandScore(false);
    }
  };

  const handleDownload = async () => {
    if (isDownloadingReport) {
      return;
    }

    try {
      setIsDownloadingReport(true);
      await downloadReport();
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const isProjectDataReady = useMemo(() => {
    if (isProjectDailyStatsLoading || isMonitorPromptsLoading || isAllBrandsStatsResponseLoading) {
      return false;
    }

    return (
      !!allBrandsStatsResponse &&
      allBrandsStatsResponse.length > 0 &&
      !!monitorPrompts &&
      monitorPrompts.length > 0 &&
      !hasAnyPendingQuestions
    );
  }, [
    allBrandsStatsResponse,
    monitorPrompts,
    hasAnyPendingQuestions,
    isProjectDailyStatsLoading,
    isMonitorPromptsLoading,
    isAllBrandsStatsResponseLoading,
  ]);

  useFirstViewTracking({
    projectId: projectIdNumber,
    accountResponse,
    isDataReady: isProjectDataReady,
  });

  if (!accountResponse || !projectResponse || !project) {
    return <></>;
  }

  if (!isMonitorPromptsLoading && monitorPrompts && monitorPrompts.length === 0) {
    return (
      <ProjectPreviewEmptyState
        firmNameOrg={firmNameOrg}
        isSuperAdmin={accountResponse.isSuperAdmin}
        currentProject={project || null}
        projectId={projectId}
        demoVersion={project?.demoVersion || null}
        accountStatus={accountResponse.accountStatus}
        canManage={!!canManage}
      />
    );
  }

  // Only block until the project shell can render. Prompts/tags/stats loading is handled in-place
  // by each component's own isLoading skeleton — so the real layout appears immediately and fills
  // in without a layout jump.
  if (!projectResponse || !accountResponse) {
    return <ProjectPreviewSkeleton />;
  }

  if (!projectIdNumber) {
    navigate(PROJECTS);
    return null;
  }

  const isAllSelected = selectedChatType === 'ALL';
  const isFiltersReady = !filtersInitializing;
  const hasProjectStatsData = hasProjectStats(projectStatsMatrix);

  const isShareOfVoiceLoading =
    !isFiltersReady ||
    isProjectDailyStatsLoading ||
    isMonitorPromptsLoading ||
    isAllBrandsStatsResponseLoading ||
    isEventsLoading;

  // Refresh flags go ONLY into components' `isLoading` props — never into the section gates above/below,
  // which would unmount whole sections on every date change.
  const isShareOfVoiceRefreshing =
    isProjectDailyStatsRefreshing || isAllBrandsStatsResponseRefreshing || isSovPerModelRefreshing;

  // Nagłówek strony: na desktopie w `Title`; na telefonie tytuł i menu projektu w górnym pasku
  // (`MobilePageHeader`), a filtry na górze strony, pod paskiem.
  const titleIconClass = `ico--md  ${
    project.localization ? `fi-${project.localization.toLowerCase()}` : 'ico--globe'
  } `;
  const headerFilters = (
    <>
      {!project.demoVersion && (
        <ChangeKeywordBox
          project={project}
          changeKeyword={onRefresh}
          liveKeys={liveBrandKeys}
        />
      )}
      <FilterPanel baseUrl={location.pathname}>
        <PromptGroupDropdown projectId={projectIdNumber} />
      </FilterPanel>
    </>
  );
  const headerMore = (
    <>
      {/* Wszystko w tym menu wychodzi poza podgląd: edycja projektu, eksporty, raport
          PDF i usunięcie projektu. Dla widza bez prawa zapisu znika w całości. */}
      {projectsAccess.write && (
      <MoreBtn rowId={0}>
        <div className="buttons-list">
          {!project?.demoVersion &&
            !['BLOCKED', 'DISABLED'].includes(accountResponse?.accountStatus) && (
              <>
                <Button
                  id={`btn-edit-project-${projectIdNumber}`}
                  className="action-btn material-symbols-outlined"
                  onClick={() => editName(projectIdNumber)}
                >
                  <span className="ico--edit ico--xs" />
                </Button>
                <CustomTooltip
                  tooltipText="editProject"
                  targetRef={`btn-edit-project-${projectIdNumber}`}
                />
                <Button
                  id={`btn-download-stats-${projectIdNumber}`}
                  className={`action-btn material-symbols-outlined ${
                    !canExport ? 'btn--disabled-tooltip' : ''
                  }`}
                  onClick={handleExport}
                >
                  {isDownloading ? (
                    <span className="ico--loader ico--xs" />
                  ) : (
                    <span
                      className={`ico--download ${
                        !canExport ? 'ico--disabled' : ''
                      } ico--xs`}
                    />
                  )}
                </Button>
                <CustomTooltip
                  tooltipText={
                    !canExport ? 'upgradePlanTrialExportMsg' : 'downloadPromptStats'
                  }
                  targetRef={`btn-download-stats-${projectIdNumber}`}
                />
                <Button
                  id={`btn-download-brand-score-${projectIdNumber}`}
                  className={`action-btn material-symbols-outlined ${
                    !canExport ? 'btn--disabled-tooltip' : ''
                  }`}
                  onClick={handleDailyStatsExport}
                >
                  {isDownloadingBrandScore ? (
                    <span className="ico--loader ico--xs" />
                  ) : (
                    <span className={`ico--download ico--xs`} />
                  )}
                </Button>
                <CustomTooltip
                  tooltipText={
                    !canExport ? 'upgradePlanTrialExportMsg' : 'downloadBrandScore'
                  }
                  targetRef={`btn-download-brand-score-${projectIdNumber}`}
                />
              </>
            )}
          {accountResponse?.addons.includes('REPORTS') && (
            <>
              <Button
                id={`btn-pdf-report-${projectIdNumber}`}
                className="action-btn material-symbols-outlined"
                onClick={handleDownload}
              >
                {isDownloadingReport ? (
                  <span className="ico--loader ico--xs" />
                ) : (
                  <span className="ico--report ico--xs" />
                )}
              </Button>
              <CustomTooltip
                tooltipText="downloadPdfReport"
                targetRef={`btn-pdf-report-${projectIdNumber}`}
              />
            </>
          )}
          {accountResponse?.isSuperAdmin && !accountResponse?.isLimitedSuperAdmin && (
            <>
              <Button
                id={`btn-regen-suggested-${projectIdNumber}`}
                className="action-btn material-symbols-outlined"
                onClick={() => setRegenerateDialogOpen(true)}
                disabled={isRegenerating}
              >
                <span className="ico--regenerate ico--xs" />
              </Button>
              <CustomTooltip
                tooltipText="regenerateSuggestedPrompts"
                targetRef={`btn-regen-suggested-${projectIdNumber}`}
              />
            </>
          )}
          <Button
            id={`btn-remove-project-${projectIdNumber}`}
            className="action-btn material-symbols-outlined"
            onClick={() => confirmRemove(projectIdNumber, project?.projectLabel)}
          >
            <span className="ico--trash ico--xs" />
          </Button>
          <CustomTooltip
            tooltipText="removeProject"
            targetRef={`btn-remove-project-${projectIdNumber}`}
          />
        </div>
      </MoreBtn>
      )}
    </>
  );

  return (
    <>
      <div className="preview-project-page page-content">
        <div className="page-box">
          <MobilePageHeader
            title={
              <>
                <span className={titleIconClass} />
                <span className="capitalize">{firmNameOrg}</span>
              </>
            }
            actions={headerMore}
          />
          {isMobile && <div className="mobile-page-filters">{headerFilters}</div>}
          {!isMobile && (
            <div className="page-box__filters" data-observe-sticky>
              <Title
                isBaseCategory={false}
                title={firmNameOrg}
                titleClassName="capitalize"
                titleStyle={titleIconClass}
                children={
                  <div className="filters">
                    {headerFilters}
                    {headerMore}
                  </div>
                }
              />
            </div>
          )}

          <>
            {hasAnyPendingQuestions && (
              <div className="data-collecting-notice">
                <div className="status status--pending">
                  <div className="status__ring" />
                  <div className="status__dot" />
                </div>
                <span className="data-collecting-notice__text">{t('dataCollectingNotice')}</span>
              </div>
            )}

            <div className="page-box__stats page-box__stats--without-bottom-padding">
              <ProjectStatsTiles
                projectGeneralData={
                  allBrandsStatsResponse.find((item) => item.keyword === firmBrandKey) || null
                }
                collecting={!hasProjectStatsData && isInitialCollecting}
                recomputing={isRecomputing}
                isLoading={
                  !isFiltersReady ||
                  isProjectDailyStatsLoading ||
                  isProjectDailyStatsRefreshing ||
                  isAllBrandsStatsResponseRefreshing ||
                  isMonitorPromptsLoading ||
                  (!hasProjectStatsData && isInitialCollecting)
                }
              />

              <div className="stats">
                <BrandPositionChart
                  averagePositionData={projectStatsMatrix?.secondMatrix ?? []}
                  brandScoreData={projectStatsMatrix?.matrix ?? []}
                  monitorHeaders={monitorHeaders}
                  eventsData={events}
                  hasAnyPendingQuestions={isInitialCollecting}
                  hasProjectStatsData={hasProjectStatsData}
                  recomputingBrands={pendingBrands}
                  isLoading={
                    !isFiltersReady ||
                    isProjectDailyStatsLoading ||
                    isProjectDailyStatsRefreshing ||
                    isMonitorPromptsLoading ||
                    isEventsLoading ||
                    (canShowAnomalyMarkers && isAnomalyIncidentsLoading)
                  }
                  averagePositionAnomalies={averagePositionAnomalies}
                  brandScoreAnomalies={brandScoreAnomalies}
                  visibilityAnomalies={visibilityAnomalies}
                  metricSemantics={metricSemantics}
                  period={monitorPeriod}
                />
                <ShareOfVoiceChart
                  key={`share-of-voice-chart-${projectId}`}
                  shareOfVoiceData={shareOfVoiceData}
                  hasAnyPendingQuestions={isInitialCollecting}
                  firmName={firmNameLabel}
                  hasProjectStatsData={hasProjectStatsData}
                  onSettingsClick={() =>
                    confirmNewProjectKeywords(
                      projectIdNumber,
                      () => project.firmName,
                      commonKeywords || [],
                      tags,
                      liveBrandKeys,
                      project.defaultKeyword
                    )
                  }
                  isLoading={isShareOfVoiceLoading || isShareOfVoiceRefreshing}
                  recomputingBrands={pendingBrands}
                />
              </div>

              {isAllSelected &&
                !isSingleModelSelected &&
                !isCommonKeywordsLoading &&
                allModelsStatsMatrix.matrix &&
                allModelsStatsMatrix.matrix.length > 0 &&
                brandColors &&
                brandColors.length > 0 && (
                  <div className="stats stats--scroll stats--full-width">
                    <ShareOfVoiceBarChart
                      allModelsStatsMatrix={allModelsStatsMatrix.matrix}
                      brandColors={brandColors}
                      firmName={firmNameLabel}
                      keywordsMapper={keywordsMap}
                      commonKeywords={commonKeywords}
                      tags={tags}
                    />
                  </div>
                )}

              {!isShareOfVoiceLoading &&
                !isCommonKeywordsLoading &&
                allBrandsStatsResponse &&
                allBrandsStatsResponse.length > 0 &&
                accountResponse.addons.includes('TABLE') && (
                  <div className="stats">
                    <BrandsTable
                      recomputingBrands={pendingBrands}
                      allBrandsStatsResponse={allBrandsStatsResponse}
                      isLoading={isAllBrandsStatsResponseRefreshing}
                      keywordsMapper={keywordsMap}
                      commonKeywords={commonKeywords}
                      tags={tags}
                      mainBrandKey={defaultBrandKey}
                      selectedBrandKey={firmBrandKey}
                    />
                    <InsightsBox
                      hasAnyPendingQuestions={isInitialCollecting}
                      visibilityTexts={visibilityTexts}
                    />
                  </div>
                )}
            </div>

            <div id="key-prompts">
              <PromptsTable
                tableTitle={t('keyPromptsTitle')}
                tableTitleSubject={t('keyPromptsFor', { name: firmNameOrg })}
                data={promptsData}
                refreshAfterDelete={onRefresh}
                range={range}
                showKeyword={false}
                showFeatures
                projectId={projectIdNumber}
                chatType={selectedChatType ?? 'GPT'}
                periodType={monitorPeriod ?? null}
                isLoading={
                  !isFiltersReady ||
                  isKeywordDailyStatsForProjectLoading ||
                  isKeywordDailyStatsForProjectRefreshing ||
                  isRecomputing
                }
                modelsWithoutData={modelsWithoutData}
                selectedChatTypes={selectedChatTypes}
                additionalClass={
                  sourcesForProject.linksData && sourcesForProject.linksData.length > 0
                    ? 'table-content--without-bottom'
                    : ''
                }
                groups={hasPromptGroupsAddon
                  ? promptGroupId === ALL_PROMPT_GROUPS_ID
                    ? (nonEmptyPromptGroups as PromptGroupItem[])
                    : (nonEmptyPromptGroups as PromptGroupItem[]).filter((g) => g.id === promptGroupId)
                  : undefined
                }
                hasPromptGroupsAddon={hasPromptGroupsAddon}
                groupDisplayMode={hasPromptGroupsAddon ? 'accordion' : undefined}
              />
            </div>

            {!isProjectDailyStatsLoading &&
              !isMonitorPromptsLoading &&
              sourcesForProject.linksData &&
              sourcesForProject.linksData?.length > 0 && (
                <LinkTrackingTable
                  exportSources={exportSources}
                  domains={sourcesForProject.domains || []}
                  sources={sourcesForProject.linksData || []}
                  isAllSelected={isAllSelected}
                  isDemo={!!project.demoVersion}
                  tags={tags ?? []}
                />
              )}
          </>
        </div>
      </div>
      {toastElement}
      <Dialog
        header={t('regenerateSuggestedPrompts')}
        visible={regenerateDialogOpen}
        onHide={() => !isRegenerating && setRegenerateDialogOpen(false)}
        className="dialog-box regenerate-prompts-dialog"
        style={{ width: '460px' }}
        dismissableMask={!isRegenerating}
      >
        <div className="regenerate-prompts-dialog__content">
          <p className="regenerate-prompts-dialog__desc">
            <Trans i18nKey="regenerateSuggestedPromptsDesc" components={{ bold: <strong /> }} />
          </p>
          <Dropdown
            value={regenerateLanguage}
            onChange={(e) => setRegenerateLanguage(e.value)}
            options={regenerateLanguageOptions}
            optionLabel="name"
            placeholder={t('forceLanguageOptional')}
            className="dropdown"
            showClear
          />
          <div className="regenerate-prompts-dialog__footer">
            <Button
              className="btn btn--sm btn--secondary"
              label={t('cancel')}
              onClick={() => setRegenerateDialogOpen(false)}
              disabled={isRegenerating}
            />
            <Button
              className="btn btn--sm"
              label={isRegenerating ? t('regenerating') : t('regenerate')}
              disabled={isRegenerating}
              onClick={async () => {
                if (isRegenerating) return;
                setIsRegenerating(true);
                try {
                  const result = await regenerateSuggestedQuestions(
                    projectIdNumber,
                    regenerateLanguage?.type ?? null
                  );
                  showToast(
                    t('regenerateSingleSuccess', { deleted: result.deletedCount }),
                    'success'
                  );
                  setRegenerateDialogOpen(false);
                  setRegenerateLanguage(null);
                } catch (err) {
                  console.error('Regenerate failed', err);
                  showToast(t('regenerateError'), 'error');
                } finally {
                  setIsRegenerating(false);
                }
              }}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ProjectsPreviewPage;
